// /scrapp-handoff: opened by the SCR App's in-app browser (src/utils/scrappHandoff.ts). Signs the person in from the
// SCR App without a Microsoft prompt, or falls back to /login?sso=entra. Already signed in: straight on to `next` or
// their portal. Public route (no guard): it must work while signed out.
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import supabase from '../../services/supabase';
import { bridgeOf, fallbackLoginUrl, landingPath, runHandoff } from '../../utils/scrappHandoff';

const FUNCTION_URL = `${String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/+$/, '')}/functions/v1/ld-scrapp-handoff`;
const ANON_KEY = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '');

// Kept outside the component so the address is read once (then cleared) and the handoff runs at most once per page
// load, whatever re-renders or remounts happen while AuthProvider resolves the new session.
let entry: { search: string; hash: string } | null = null;
let attempted = false;
let handedOff = false;

/**
 * The app's post-sign-in gate, the same check AuthContext runs after any sign-in (Microsoft included): the user must
 * have a learning_diversity.profiles row. An error counts as no profile, as it does there.
 */
async function hasProfile(): Promise<boolean> {
  const { data: auth, error: userError } = await supabase.auth.getUser();
  if (userError || !auth.user) return false;
  const { data, error } = await supabase.from('profiles').select('id').eq('id', auth.user.id).maybeSingle();
  return !error && !!data;
}

export const ScrAppHandoff = () => {
  const { user, session, loading } = useAuth();
  if (!entry) {
    entry = { search: window.location.search, hash: window.location.hash };
    // Strip next and the College email from the address bar and history before anything else runs.
    window.history.replaceState(window.history.state, '', '/scrapp-handoff');
  }
  const { search, hash } = entry;

  // AuthContext runs the same profile check on the new session and signs out if it fails (e.g. a network error on
  // its own fetch). Then nobody is signed in after all: fall back rather than spin.
  useEffect(() => {
    if (handedOff && !loading && !user && !session) window.location.replace(fallbackLoginUrl(search, hash));
  }, [loading, user, session, search, hash]);

  useEffect(() => {
    // Wait for the session check; someone already signed in (or mid profile check) is not handed off again.
    if (loading || user || session || attempted) return;
    attempted = true;
    void (async () => {
      const ok = await runHandoff({
        bridge: bridgeOf(window),
        exchange: async (creds) => {
          const res = await fetch(FUNCTION_URL, {
            method: 'POST',
            credentials: 'omit',
            cache: 'no-store',
            referrerPolicy: 'no-referrer',
            headers: {
              'Content-Type': 'application/json',
              apikey: ANON_KEY,
              Authorization: `Bearer ${ANON_KEY}`,
              'X-SCRApp-Handoff': '1',
            },
            body: JSON.stringify(creds),
          });
          return res.ok ? res.json() : null;
        },
        setSession: async (s) => !(await supabase.auth.setSession(s)).error,
        authorize: hasProfile,
        signOut: async () => {
          await supabase.auth.signOut({ scope: 'local' });
        },
      });
      // On success AuthContext resolves the same profile and sets `user`; the render below then moves on.
      if (ok) handedOff = true;
      else window.location.replace(fallbackLoginUrl(search, hash));
    })();
  }, [loading, user, session, search, hash]);

  if (user) return <Navigate to={landingPath(search, user.role)} replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-live="polite">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Signing you in…</p>
      </div>
    </div>
  );
};
